import React from 'react'
import {
  Typography,
  List,
  ListItemButton,
  ListItemText,
  Box,
  CircularProgress,
  Alert
} from '@mui/material'
import { PayPeriodData } from '../../../../models/enhancement/pay-period-data'
import { formatPayPeriodDateRange } from '../../../../utils/payPeriodFormatting'

interface PayPeriodModalContentProps {
  periodsForYear: PayPeriodData[]
  selectedYear: number
  isLoading: boolean
  error: string | null
  onSelect: (payPeriod: PayPeriodData) => void
  dataTestId?: string
}

function PayPeriodModalContent({
  periodsForYear,
  selectedYear,
  isLoading,
  error,
  onSelect,
  dataTestId = 'modal-content'
}: PayPeriodModalContentProps) {
  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" py={4}>
        <CircularProgress />
      </Box>
    )
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ m: 1 }}>
        {error}
      </Alert>
    )
  }

  if (periodsForYear.length === 0) {
    return (
      <Typography
        variant="body2"
        color="text.secondary"
        textAlign="center"
        py={4}
        data-testid={`${dataTestId}-no-periods`}
      >
        No pay periods available for
        {' '}
        {selectedYear}
      </Typography>
    )
  }

  return (
    <List
      sx={{
        maxHeight: '300px',
        overflow: 'auto',
        '& .MuiListItemButton-root': {
          borderRadius: 1,
          mx: 1,
          mb: 0.5,
          '&:hover': {
            backgroundColor: 'action.hover',
          }
        }
      }}
      data-testid={`${dataTestId}-periods-list`}
    >
      {periodsForYear.map((payPeriod) => (
        <ListItemButton
          key={`${payPeriod.year}-${payPeriod.period}`}
          onClick={() => onSelect(payPeriod)}
          data-testid={`${dataTestId}-period-${payPeriod.year}-${payPeriod.period}`}
        >
          <ListItemText
            primary={
              <Typography variant="body1" fontWeight="medium">
                Pay Period
                {' '}
                {payPeriod.period}
              </Typography>
            }
            secondary={
              <Typography variant="body2" color="text.secondary">
                {formatPayPeriodDateRange(payPeriod)}
              </Typography>
            }
          />
        </ListItemButton>
      ))}
    </List>
  )
}

export default PayPeriodModalContent